import './FilterRow.scss';

import { IconClose, IconDelete, IconDrag } from '@hulu-react-style-components/icons';
import { Radio } from '@hulu/react-style-components';
import React from 'react';
import type { DraggableProvidedDraggableProps, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

import type { FilterConfig, FilterOperator, FilterOption } from '../../../../apis/graphql/types';
import { FilterOperator as FilterOperatorEnum, FilterType } from '../../../../apis/graphql/types';
import Pill, { PillColor } from '../../../../common/Pill/Pill';
import bem from '../../../../utils/bem';
import DriftWarning from '../CustomSavedFilters/DriftWarning';
import { isMultiSelectConfig } from '../multiSelectFilterUtils';
import type { DateFilter } from './DateControl/DateControl';
import DateControl from './DateControl/DateControl';
import type { OperatorValueState } from './OperatorValueRangeControl/OperatorValueRangeControl';
import OperatorValueRangeControl from './OperatorValueRangeControl/OperatorValueRangeControl';
import SearchableMultiSelectControl from './SearchableMultiSelectControl/SearchableMultiSelectControl';
import StaticMultiSelectControl from './StaticMultiSelectControl/StaticMultiSelectControl';

// Discriminator note: campaign-graphql currently emits boolean filters with
// `type: 'RADIOBUTTON'` and `isBoolean: true`. We key off `isBoolean` rather
// than `type` to match the practical signal in filterConfigData, consistent
// with the existing `mapBooleanFilterConfig` mapper.

const DEFAULT_TRUE_LABEL = 'Yes';
const DEFAULT_FALSE_LABEL = 'No';

type BooleanLabels = { trueLabel: string; falseLabel: string };

function getBooleanLabelsFromConfig(config: FilterConfig): BooleanLabels {
  if (config.staticOptions && config.staticOptions.length === 2) {
    return {
      trueLabel: config.staticOptions[0].label,
      falseLabel: config.staticOptions[1].label,
    };
  }
  return { trueLabel: DEFAULT_TRUE_LABEL, falseLabel: DEFAULT_FALSE_LABEL };
}

/**
 * Staged value for multi-select controls that support Is / Is Not operator selection.
 * Only produced when config.includeExclude?.excludeKey is defined — plain multi-selects
 * without negation support continue to stage FilterOption[].
 */
export type MultiSelectState = {
  operator: FilterOperator;
  options: FilterOption[];
};

export type RangeInputNotice = {
  tone: 'hint' | 'warning';
  message: string;
};

export type FilterRowValue = boolean | DateFilter | OperatorValueState | MultiSelectState | FilterOption[] | null;

export function isMultiSelectState(value: FilterRowValue): value is MultiSelectState {
  return (
    value !== null && typeof value === 'object' && !Array.isArray(value) && 'options' in value && 'operator' in value
  );
}

export type FilterRowProps = {
  config: FilterConfig;
  value: FilterRowValue;
  onChange: (id: string, value: FilterRowValue) => void;
  onRemove: (id: string) => void;
  rangeDecimals?: number;
  rangeNotice?: RangeInputNotice;
  innerRef?: (element: HTMLElement | null) => void;
  draggableProps?: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
};

const [block, element] = bem('filter-row');

type BooleanControlProps = {
  config: FilterConfig;
  value: FilterRowValue;
  onChange: (id: string, value: FilterRowValue) => void;
};

const BooleanRadioControl = ({ config, value, onChange }: BooleanControlProps): JSX.Element => {
  const { trueLabel, falseLabel } = getBooleanLabelsFromConfig(config);

  return (
    <div className={element('control', 'radio')} data-testid="filter-row-radio-control">
      <Radio
        id={`${config.id}-true`}
        isChecked={value === true}
        isErr={false}
        label={trueLabel}
        onChange={(): void => onChange(config.id, true)}
      />
      <Radio
        id={`${config.id}-false`}
        isChecked={value === false}
        isErr={false}
        label={falseLabel}
        onChange={(): void => onChange(config.id, false)}
      />
    </div>
  );
};

function toMultiSelectState(value: FilterRowValue, operators?: FilterOperator[] | null): MultiSelectState {
  if (isMultiSelectState(value)) return value;
  return {
    operator: operators?.[0] ?? FilterOperatorEnum.In,
    options: Array.isArray(value) ? (value as FilterOption[]) : [],
  };
}

const renderControl = (
  config: FilterConfig,
  value: FilterRowValue,
  onChange: (id: string, value: FilterRowValue) => void,
  rangeDecimals?: number
): JSX.Element | null => {
  if (config.isBoolean) {
    return <BooleanRadioControl config={config} value={value as boolean | null} onChange={onChange} />;
  }
  if (config.isSearchable) {
    return (
      <SearchableMultiSelectControl
        config={config}
        value={toMultiSelectState(value, config.operators)}
        onChange={onChange}
      />
    );
  }
  if (config.type === FilterType.Calendar) {
    return (
      <DateControl
        config={config}
        value={value as DateFilter}
        onChange={(id, dateValue): void => onChange(id, dateValue)}
      />
    );
  }
  if (config.type === FilterType.Range) {
    const operatorValue: OperatorValueState =
      value && typeof value === 'object' && !Array.isArray(value) && 'operator' in value && 'value' in value
        ? (value as OperatorValueState)
        : { operator: null, value: null };
    return (
      <OperatorValueRangeControl config={config} value={operatorValue} onChange={onChange} decimals={rangeDecimals} />
    );
  }
  // Remaining types are non-boolean, non-searchable, non-Calendar, non-Range —
  // i.e. CHECKBOX/CHECKBOXES/DROPDOWN/RADIOBUTTON enum lists rendered as a
  // non-searchable multi-select. Options come from staticOptions or a one-shot
  // getFilterOptions fetch, decided inside the control by optionSource.
  return (
    <StaticMultiSelectControl config={config} value={toMultiSelectState(value, config.operators)} onChange={onChange} />
  );
};

export function getSelectedOptions(value: FilterRowValue): FilterOption[] {
  if (isMultiSelectState(value)) return value.options;
  if (Array.isArray(value)) return value as FilterOption[];
  return [];
}

const FilterRow = ({
  config,
  value,
  onChange,
  onRemove,
  rangeDecimals,
  rangeNotice,
  innerRef,
  draggableProps,
  dragHandleProps,
  isDragging,
}: FilterRowProps): JSX.Element => {
  const selectedOptions = isMultiSelectConfig(config) ? getSelectedOptions(value) : [];

  const handleRemoveValue = (optionValue: string): void => {
    if (isMultiSelectState(value)) {
      onChange(config.id, { operator: value.operator, options: value.options.filter((o) => o.value !== optionValue) });
    } else if (Array.isArray(value)) {
      onChange(
        config.id,
        (value as FilterOption[]).filter((o) => o.value !== optionValue)
      );
    }
  };

  const handleClearAll = (): void => {
    if (isMultiSelectState(value)) {
      onChange(config.id, { operator: value.operator, options: [] });
    } else if (Array.isArray(value)) {
      onChange(config.id, []);
    }
  };

  return (
    <div
      ref={innerRef}
      className={block(isDragging ? 'dragging' : undefined)}
      data-testid={`filter-row-${config.id}`}
      {...draggableProps}
    >
      <div className={element('main')}>
        <span
          className={element('drag-handle')}
          aria-label={`drag to reorder ${config.label} filter`}
          role="button"
          {...dragHandleProps}
        >
          <IconDrag width="12px" height="12px" />
        </span>
        <span className={element('label')}>{config.label}</span>
        <div className={element('control-area')}>
          {renderControl(config, value, onChange, rangeDecimals)}
          {selectedOptions.length > 0 && (
            <button
              type="button"
              className={element('clear-btn')}
              onClick={handleClearAll}
              data-testid={`filter-row-clear-all-${config.id}`}
            >
              Clear All
            </button>
          )}
        </div>
        <button
          type="button"
          className={element('remove-btn')}
          onClick={(): void => onRemove(config.id)}
          aria-label={`remove ${config.label} filter`}
        >
          <IconDelete width="16px" height="16px" />
        </button>
      </div>
      {selectedOptions.length > 0 && (
        <div className={element('chips')} data-testid={`filter-row-chips-${config.id}`}>
          {selectedOptions.map((option) => (
            <Pill
              key={option.value}
              color={PillColor.BLUE}
              className={element('chip')}
              textComponent={
                <span className={element('chip-content')} data-testid={`filter-chip-${option.value}`}>
                  <span className={element('chip-label')}>{option.label}</span>
                  <button
                    type="button"
                    className={element('chip-remove')}
                    onClick={(): void => handleRemoveValue(option.value)}
                    aria-label={`remove ${option.label}`}
                  >
                    <IconClose width="10px" height="10px" />
                  </button>
                </span>
              }
            />
          ))}
        </div>
      )}
      {rangeNotice && (
        <div className={element('notice')} data-testid={`filter-row-notice-${config.id}`}>
          {rangeNotice.tone === 'warning' ? (
            <DriftWarning message={rangeNotice.message} />
          ) : (
            <span className={element('hint')}>{rangeNotice.message}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterRow;
